import {
  DownOutlined,
  FilterFilled,
  Loading3QuartersOutlined,
  SearchOutlined,
  UpOutlined
} from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Input, Spin } from 'antd';
// eslint-disable-next-line no-use-before-define
import * as React from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import {
  AssigneesFilter,
  MoreFilters,
  PageFilter,
  ProjectSearchFilters,
  ProjectSearchTag,
  ProjectSearchUser,
  PROJECT_SEARCH_SEVERITIES,
  SearchResultsSummary
} from '../../../../../clients/shared/ProjectSearch';
import sharedStyles from '../../../../../clients/shared/ProjectSearch/filters.module.css';

import { useGetUserProjectQuery } from '../FilterTask/ProjectFilter/query';
import { useGetOrganizationUsers } from '../FilterTask/RequesterFilter/query';
import {
  countActiveDashboardTaskSearchFilters,
  DashboardTaskSearchFilters,
  DashboardTaskSearchPagination
} from './dashboardTaskSearchState';
import styles from './DashboardTaskSearchShell.module.css';

type Props = {
  filters: DashboardTaskSearchFilters;
  committedQuery?: string;
  currentUser: ProjectSearchUser | null;
  onClear: () => void;
  onFiltersChange: (filters: DashboardTaskSearchFilters) => void;
  onQueryChange: (query: string) => void;
  onQuerySubmit: (query: string) => void;
  organizationId: string;
  pagination?: DashboardTaskSearchPagination;
  query: string;
  statusNames: string[];
  tagNames: string[];
};

type SearchableProject = {
  id: string;
  name: string;
};

const FILTER_OPTIONS_LIMIT = 100;
const SEARCH_INPUT_THRESHOLD = 5;
const PROJECT_ROW_HEIGHT = 40;
const MAX_PROJECT_LIST_HEIGHT = 280;

const toggleToken = (tokens: string[], token: string) =>
  tokens.includes(token)
    ? tokens.filter(value => value !== token)
    : [...tokens, token];

export const sortProjectsBySelected = (
  projects: SearchableProject[],
  selectedProjectIds: string[] = []
) => {
  const selectedProjects = new Set(selectedProjectIds);

  return [...projects].sort(
    (firstProject, secondProject) =>
      Number(!selectedProjects.has(firstProject.id)) -
      Number(!selectedProjects.has(secondProject.id))
  );
};

const mergeSharedFilters = (
  filters: DashboardTaskSearchFilters,
  nextFilters: ProjectSearchFilters
): DashboardTaskSearchFilters => ({
  ...filters,
  ...nextFilters,
  project: filters.project
});

export const DashboardProjectFilter: React.FC<{
  filters: DashboardTaskSearchFilters;
  getPopupContainer: (triggerNode: HTMLElement) => HTMLElement;
  onChange: (filters: DashboardTaskSearchFilters) => void;
  organizationId: string;
}> = ({ filters, getPopupContainer, onChange, organizationId }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [
    selectedProjectIdsAtOpen,
    setSelectedProjectIdsAtOpen
  ] = React.useState<string[]>([]);
  const { data, loading } = useGetUserProjectQuery({
    organizationId,
    offset: 0,
    limit: FILTER_OPTIONS_LIMIT,
    filter: query ? { name: query } : undefined
  });
  const projects: SearchableProject[] = data?.currentUser.userProjects || [];
  const sortedProjects = sortProjectsBySelected(
    projects,
    selectedProjectIdsAtOpen
  );
  const projectCount = data?.currentUser.userProjectsCount || 0;
  const showProjectSearch = !!query || projectCount > SEARCH_INPUT_THRESHOLD;
  const count = filters.project.length;
  let projectOptions = <p className={styles.noResults}>No projects found</p>;

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (nextIsOpen) setSelectedProjectIdsAtOpen(filters.project);
    setIsOpen(nextIsOpen);
  };

  const renderProject = ({ index, style }: ListChildComponentProps) => {
    const project = sortedProjects[index];

    return (
      <div style={style}>
        <label className={styles.pickerRow}>
          <Checkbox
            checked={filters.project.includes(project.id)}
            onChange={() =>
              onChange({
                ...filters,
                project: toggleToken(filters.project, project.id)
              })
            }
          />
          <span>{project.name}</span>
        </label>
      </div>
    );
  };

  if (loading) {
    projectOptions = (
      <div className={styles.pickerLoading}>
        <Spin
          indicator={<Loading3QuartersOutlined style={{ fontSize: 16 }} spin />}
        />
      </div>
    );
  } else if (sortedProjects.length) {
    projectOptions = (
      <FixedSizeList
        className={styles.pickerVirtualList}
        height={Math.min(
          MAX_PROJECT_LIST_HEIGHT,
          sortedProjects.length * PROJECT_ROW_HEIGHT
        )}
        itemCount={sortedProjects.length}
        itemSize={PROJECT_ROW_HEIGHT}
        width="100%"
      >
        {renderProject}
      </FixedSizeList>
    );
  }

  return (
    <Dropdown
      arrow={{ pointAtCenter: true }}
      getPopupContainer={getPopupContainer}
      onOpenChange={handleOpenChange}
      open={isOpen}
      overlay={
        <div className={styles.pickerPanel}>
          {showProjectSearch && (
            <Input
              aria-label="Search projects"
              className={styles.pickerSearchInput}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search projects..."
              prefix={<SearchOutlined />}
              value={query}
            />
          )}
          <div className={styles.pickerHeading}>Projects</div>
          <div className={styles.pickerList}>{projectOptions}</div>
        </div>
      }
      overlayClassName={styles.pickerOverlay}
      placement="bottomLeft"
      trigger={['click']}
    >
      <Button
        className={sharedStyles.filterPill}
        shape="round"
        size="small"
        type={isOpen || count ? 'primary' : 'default'}
      >
        {count ? `Project (${count})` : 'Project'}
        {isOpen ? <UpOutlined /> : <DownOutlined />}
      </Button>
    </Dropdown>
  );
};

export const DashboardStatusFilter: React.FC<{
  filters: DashboardTaskSearchFilters;
  getPopupContainer: (triggerNode: HTMLElement) => HTMLElement;
  onChange: (filters: DashboardTaskSearchFilters) => void;
  statusNames: string[];
}> = ({ filters, getPopupContainer, onChange, statusNames }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const statuses = React.useMemo(
    () =>
      statusNames
        .filter((name, index) => !!name && statusNames.indexOf(name) === index)
        .sort((first, second) => first.localeCompare(second)),
    [statusNames]
  );
  const visibleStatuses = React.useMemo(
    () =>
      statuses.filter(status =>
        status.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [query, statuses]
  );
  const showStatusSearch = statuses.length > SEARCH_INPUT_THRESHOLD;
  const count = filters.status.length;

  return (
    <Dropdown
      arrow={{ pointAtCenter: true }}
      getPopupContainer={getPopupContainer}
      onOpenChange={setIsOpen}
      open={isOpen}
      overlay={
        <div className={styles.statusPanel}>
          {showStatusSearch && (
            <Input
              aria-label="Search statuses"
              className={styles.pickerSearchInput}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search statuses..."
              prefix={<SearchOutlined />}
              value={query}
            />
          )}
          {visibleStatuses.length ? (
            visibleStatuses.map(status => (
              <label className={styles.pickerRow} key={status}>
                <Checkbox
                  checked={filters.status.includes(status)}
                  onChange={() =>
                    onChange({
                      ...filters,
                      status: toggleToken(filters.status, status)
                    })
                  }
                />
                <span>{status}</span>
              </label>
            ))
          ) : (
            <p className={styles.noResults}>No statuses found</p>
          )}
        </div>
      }
      overlayClassName={styles.statusOverlay}
      placement="bottomLeft"
      trigger={['click']}
    >
      <Button
        className={sharedStyles.filterPill}
        shape="round"
        size="small"
        type={isOpen || count ? 'primary' : 'default'}
      >
        {count ? `Status (${count})` : 'Status'}
        {isOpen ? <UpOutlined /> : <DownOutlined />}
      </Button>
    </Dropdown>
  );
};

export const DashboardAssigneesFilter: React.FC<{
  currentUser: ProjectSearchUser | null;
  filters: DashboardTaskSearchFilters;
  getPopupContainer: (triggerNode: HTMLElement) => HTMLElement;
  onChange: (filters: DashboardTaskSearchFilters) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  organizationId: string;
}> = ({
  currentUser,
  filters,
  getPopupContainer,
  onChange,
  onOpenChange,
  open,
  organizationId
}) => {
  const [query, setQuery] = React.useState('');
  const { data } = useGetOrganizationUsers({
    organizationId,
    offset: 0,
    limit: FILTER_OPTIONS_LIMIT,
    filter: query ? { name: query } : undefined
  });
  const users: ProjectSearchUser[] =
    data?.currentUser.organization.users.map(user => ({
      id: Number(user.id),
      name: user.name,
      email: user.email,
      avatarUrl: user.avatarUrl
    })) || [];

  return (
    <AssigneesFilter
      currentUser={currentUser}
      filters={filters as ProjectSearchFilters}
      getPopupContainer={getPopupContainer}
      includeInvolves={false}
      onChange={nextFilters =>
        onChange(mergeSharedFilters(filters, nextFilters))
      }
      onOpenChange={onOpenChange}
      onUserSearchQueryChange={setQuery}
      open={open}
      users={users}
    />
  );
};

export const DashboardMoreFilters: React.FC<{
  currentUser: ProjectSearchUser | null;
  filters: DashboardTaskSearchFilters;
  getPopupContainer: (triggerNode: HTMLElement) => HTMLElement;
  onChange: (filters: DashboardTaskSearchFilters) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  organizationId: string;
  tags: ProjectSearchTag[];
}> = ({
  currentUser,
  filters,
  getPopupContainer,
  onChange,
  onOpenChange,
  open,
  organizationId,
  tags
}) => {
  const [query, setQuery] = React.useState('');
  const { data } = useGetOrganizationUsers({
    organizationId,
    offset: 0,
    limit: FILTER_OPTIONS_LIMIT,
    filter: query ? { name: query } : undefined
  });
  const users: ProjectSearchUser[] =
    data?.currentUser.organization.users.map(user => ({
      id: Number(user.id),
      name: user.name,
      email: user.email,
      avatarUrl: user.avatarUrl
    })) || [];

  return (
    <MoreFilters
      compact
      currentUser={currentUser}
      filters={filters as ProjectSearchFilters}
      getPopupContainer={getPopupContainer}
      onChange={nextFilters =>
        onChange(mergeSharedFilters(filters, nextFilters))
      }
      onOpenChange={onOpenChange}
      onUserSearchQueryChange={setQuery}
      open={open}
      severities={PROJECT_SEARCH_SEVERITIES}
      tags={tags}
      users={users}
    />
  );
};

const DashboardTaskSearchShell: React.FC<Props> = ({
  committedQuery,
  currentUser,
  filters,
  onClear,
  onFiltersChange,
  onQueryChange,
  onQuerySubmit,
  organizationId,
  pagination,
  query,
  statusNames,
  tagNames
}) => {
  const shellRef = React.useRef<HTMLElement>(null);
  const [assigneesOpen, setAssigneesOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const filterCount = countActiveDashboardTaskSearchFilters(filters);
  const activeQuery =
    committedQuery === undefined ? query : committedQuery;
  const isActive = !!activeQuery.trim() || filterCount > 0;
  const getPopupContainer = () => shellRef.current || document.body;
  const tags = React.useMemo(
    () =>
      tagNames
        .filter(Boolean)
        .map(name => ({ id: name, label: name }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [tagNames]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onQuerySubmit(query);
    }
  };

  return (
    <section aria-label="Search tasks" className={styles.shell} ref={shellRef}>
      <Input
        aria-label="Search tasks"
        className={styles.searchInput}
        onChange={event => onQueryChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search tasks"
        prefix={<SearchOutlined />}
        suffix={
          filterCount ? (
            <span className={styles.filterCount}>
              <FilterFilled />
              {filterCount}
            </span>
          ) : null
        }
        value={query}
      />
      <div className={styles.filtersRow}>
        <PageFilter
          compact
          filters={filters as ProjectSearchFilters}
          getPopupContainer={getPopupContainer}
          onChange={nextFilters =>
            onFiltersChange(mergeSharedFilters(filters, nextFilters))
          }
        />
        <DashboardProjectFilter
          filters={filters}
          getPopupContainer={getPopupContainer}
          onChange={onFiltersChange}
          organizationId={organizationId}
        />
        <DashboardStatusFilter
          filters={filters}
          getPopupContainer={getPopupContainer}
          onChange={onFiltersChange}
          statusNames={statusNames}
        />
        <DashboardAssigneesFilter
          currentUser={currentUser}
          filters={filters}
          getPopupContainer={getPopupContainer}
          onChange={onFiltersChange}
          onOpenChange={open => {
            setAssigneesOpen(open);
            if (open) setMoreOpen(false);
          }}
          open={assigneesOpen}
          organizationId={organizationId}
        />
        <DashboardMoreFilters
          currentUser={currentUser}
          filters={filters}
          getPopupContainer={getPopupContainer}
          onChange={onFiltersChange}
          onOpenChange={open => {
            setMoreOpen(open);
            if (open) setAssigneesOpen(false);
          }}
          open={moreOpen}
          organizationId={organizationId}
          tags={tags}
        />
        {isActive && pagination && (
          <SearchResultsSummary
            className={styles.resultsSummary}
            onClear={onClear}
            resultCount={pagination.filteredCount}
            totalCount={pagination.unfilteredCount}
          />
        )}
      </div>
    </section>
  );
};

export default DashboardTaskSearchShell;
